import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  EnvelopeSimple,
  Phone,
  MapPin,
  ArrowUpRight,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { Section, FadeIn } from "../components/Section";
import Button from "../components/Button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.subject) delete payload.subject;
      const { data } = await axios.post(`${API}/contact`, payload);
      setStatus({
        state: "success",
        message: `Thank you, ${data.name}. We'll be in touch shortly.`,
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : String(detail);
      setStatus({ state: "error", message });
    }
  };

  return (
    <>
      <PageHero
        overline="Contact"
        title="Let's talk energy."
        description="Whether you're scoping a pipeline corridor, sourcing fuel for a fleet, or planning a gas-fired power project — our team is ready to help."
        image="https://images.pexels.com/photos/9806482/pexels-photo-9806482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
        testid="contact-hero"
      />

      <Section spacing="py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Info */}
          <div className="lg:col-span-5">
            <FadeIn>
              <div className="overline text-primary mb-4">— Reach us</div>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05]">
                Three ways to start the conversation.
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                Visit our Lagos head office, give us a call during business
                hours, or drop a note via the form. Most enquiries receive a
                reply within one working day.
              </p>

              <ul className="mt-10 space-y-6">
                <li className="flex items-start gap-4">
                  <div className="grid place-items-center w-12 h-12 rounded-sm bg-primary text-primary-foreground shrink-0">
                    <MapPin size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="overline text-muted-foreground mb-1">
                      Head office
                    </div>
                    <div className="text-foreground leading-relaxed">
                      11th Floor, Foreshore Towers
                      <br />
                      2a Osborne Road, Ikoyi
                      <br />
                      Lagos, Nigeria
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="grid place-items-center w-12 h-12 rounded-sm bg-primary text-primary-foreground shrink-0">
                    <Phone size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="overline text-muted-foreground mb-1">
                      Phone
                    </div>
                    <a
                      href="tel:+2348099992485"
                      className="text-foreground hover:text-primary transition-colors"
                    >
                      +234 809 999 2485
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="grid place-items-center w-12 h-12 rounded-sm bg-primary text-primary-foreground shrink-0">
                    <EnvelopeSimple size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="overline text-muted-foreground mb-1">
                      Email
                    </div>
                    <a
                      href="mailto:info@fradrogroup.com"
                      className="text-foreground hover:text-primary transition-colors"
                    >
                      info@fradrogroup.com
                    </a>
                  </div>
                </li>
              </ul>

              {/* Map placeholder */}
              <div className="mt-10 border border-border overflow-hidden h-56 relative bg-muted">
                <iframe
                  title="Fradro Group Office"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=3.4280%2C6.4490%2C3.4480%2C6.4590&amp;layer=mapnik&amp;marker=6.4540%2C3.4380"
                  className="w-full h-full grayscale"
                  loading="lazy"
                />
              </div>
            </FadeIn>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-border bg-card p-8 md:p-10"
              >
                <div className="overline text-primary mb-3">— Make an enquiry</div>
                <h3 className="font-display font-bold text-2xl md:text-3xl mb-8 tracking-tight">
                  Send us a message
                </h3>

                <div className="grid md:grid-cols-2 gap-5">
                  <Field
                    label="Full name"
                    name="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    testid="contact-name"
                  />
                  <Field
                    label="Email address"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    testid="contact-email"
                  />
                  <Field
                    label="Phone (optional)"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    testid="contact-phone"
                  />
                  <Field
                    label="Subject (optional)"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    testid="contact-subject"
                  />
                </div>

                <div className="mt-5">
                  <label className="block">
                    <span className="overline text-muted-foreground">
                      Message
                    </span>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={onChange}
                      placeholder="Tell us about your project, location and timeline…"
                      data-testid="contact-message"
                      className="mt-2 w-full bg-background border border-input px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-y rounded-sm"
                    />
                  </label>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button
                    type="submit"
                    size="lg"
                    iconRight={ArrowUpRight}
                    disabled={status.state === "loading"}
                    data-testid="contact-submit"
                  >
                    {status.state === "loading" ? "Sending…" : "Send message"}
                  </Button>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    By submitting this form you consent to be contacted by
                    Fradro Group regarding your enquiry.
                  </p>
                </div>

                {status.state === "success" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex items-start gap-3 p-4 border border-primary/40 bg-primary/10 text-foreground"
                    data-testid="contact-success"
                  >
                    <CheckCircle
                      weight="fill"
                      size={22}
                      className="text-primary shrink-0"
                    />
                    <span className="text-sm">{status.message}</span>
                  </motion.div>
                )}
                {status.state === "error" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex items-start gap-3 p-4 border border-destructive/50 bg-destructive/10 text-foreground"
                    data-testid="contact-error"
                  >
                    <WarningCircle
                      weight="fill"
                      size={22}
                      className="text-destructive shrink-0"
                    />
                    <span className="text-sm">{status.message}</span>
                  </motion.div>
                )}
              </form>
            </FadeIn>
          </div>
        </div>
      </Section>
    </>
  );
};

const Field = ({ label, name, type = "text", testid, ...rest }) => (
  <label className="block">
    <span className="overline text-muted-foreground">{label}</span>
    <input
      type={type}
      name={name}
      data-testid={testid}
      className="mt-2 w-full bg-background border border-input px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none rounded-sm"
      {...rest}
    />
  </label>
);

export default Contact;
